/**
 * Sequence Reply Engine — reply-handling autopilot for governed outreach.
 *
 * Closes the #1 lead-burning bug: a contact replies and the cadence keeps
 * firing anyway. This module owns the whole loop as PURE logic:
 *
 *   1. {@link classifyReply} — asks the REAL Argo agent-worker (injected —
 *      the same seam as src/lib/agent/argoWorker.ts / lostLeadResurrection)
 *      to classify the reply's intent + sentiment. ANY failure — worker
 *      unconfigured, thrown error, malformed payload — degrades to the
 *      deterministic {@link heuristicIntent} keyword/date classifier, and
 *      the result honestly labels which engine produced it
 *      (`source: 'worker' | 'fallback'`).
 *   2. {@link handleReply} — a pure decision function mapping a classified
 *      reply to governed {@link ReplyAction}s. Positive replies and
 *      unsubscribes auto-unenroll across ALL active sequences as a
 *      REVERSIBLE, ledgered action; OOO pauses with a scheduled resume;
 *      objections route a task to a human (task-router discipline: the
 *      owner is injected, never guessed) plus a playbook-grounded rebuttal
 *      draft that goes to the APPROVAL queue — never a send.
 *   3. {@link buildAutoUnenrollReversal} — seals the prior enrollment list
 *      as a ledger-entry before-state and compiles the compensating
 *      `restore_enrollments` proposal (reversal.ts discipline: a reversal
 *      is just another governed proposal), so the "every write reversible"
 *      invariant survives the email boundary.
 *
 * Deliberately PURE at the module boundary: worker, task router, clock and
 * enrollments are all injected. No store, no firebase, no config.
 */

import { fnvHash } from '../ledger/verifyChain';
import { REVERSAL_NOTE_PREFIX } from '../agent/reversal';
import { classifyReversibility, type Reversibility } from '../agent/reversibility';
import type { CreateTaskAction } from '../agent/types';
import { ONBOARDING_PLAYBOOK, type PlaybookStep } from './playbook';

const DAY_MS = 86_400_000;

/** When an OOO reply carries no parseable return date, resume after 7 days. */
export const DEFAULT_OOO_RESUME_DAYS = 7;

// ===========================================================================
// Types
// ===========================================================================

export type ReplyIntent =
  | 'interested'
  | 'objection'
  | 'ooo'
  | 'unsubscribe'
  | 'wrong_person'
  | 'neutral';

const REPLY_INTENTS: ReadonlyArray<ReplyIntent> = [
  'interested',
  'objection',
  'ooo',
  'unsubscribe',
  'wrong_person',
  'neutral',
];

/** An inbound reply from a sequenced contact, as the caller observed it. */
export interface InboundReply {
  contactId: string;
  /** The sequence whose send drew the reply. */
  sequenceId: string;
  /** Raw reply text (body or preview) — the classification input. */
  text: string;
  /** Epoch ms the reply arrived; wall clock when omitted. */
  at?: number;
  contactName?: string;
  accountName?: string;
}

/** The classified verdict — who said what, how sure, and which engine. */
export interface ClassifiedReply {
  contactId: string;
  sequenceId: string;
  intent: ReplyIntent;
  /** 0..1 — clamped. */
  confidence: number;
  /** Epoch ms after which a paused enrollment may resume (OOO only). */
  resumeAfter?: number;
  source: 'worker' | 'fallback';
}

// --- Reply actions (pure decisions — the caller executes/queues them) ------

/** Sealed ledger entry for an auto-unenroll: the before-state IS the undo. */
export interface AutoUnenrollLedgerEntry {
  id: string;
  /** ISO-8601 timestamp the decision was compiled. */
  at: string;
  actionType: 'auto_unenroll';
  recordId: string;
  before: { enrollments: string[] };
}

/** The compensating governed proposal that restores prior enrollments. */
export interface RestoreEnrollmentsProposal {
  id: string;
  action: {
    kind: 'restore_enrollments';
    contactId: string;
    sequenceIds: string[];
  };
  origin: 'reversal';
  reversesEntryId: string;
  /** Carries {@link REVERSAL_NOTE_PREFIX} so the approvals queue can tell. */
  note: string;
  status: 'pending';
}

/** Everything needed to undo an auto-unenroll: entry + restore + class. */
export interface ReversalPlan {
  entry: AutoUnenrollLedgerEntry;
  restore: RestoreEnrollmentsProposal;
  reversibility: Reversibility;
}

export interface AutoUnenrollAction {
  kind: 'auto_unenroll';
  contactId: string;
  /** EVERY sequence the contact was actively enrolled in. */
  sequenceIds: string[];
  reason: string;
  reversible: true;
  /** Ledgered + compiled undo — never an unrecoverable state change. */
  reversal: ReversalPlan;
}

export interface PauseEnrollmentAction {
  kind: 'pause_enrollment';
  contactId: string;
  sequenceId: string;
  /** Epoch ms of the scheduled resume; absent = paused for human review. */
  resumeAfter?: number;
  reason: string;
}

export interface RouteTaskAction {
  kind: 'route_task';
  /** A REAL CreateTaskAction, ready for the approvals executor. */
  task: CreateTaskAction;
  /** Owner picked by the injected router; null = leave for triage. */
  owner: string | null;
  note: string;
}

export interface RebuttalDraft {
  subject: string;
  body: string;
  /** Playbook step titles the rebuttal is grounded in — never invented. */
  groundedIn: string[];
}

export interface QueueRebuttalDraftAction {
  kind: 'queue_rebuttal_draft';
  contactId: string;
  sequenceId: string;
  draft: RebuttalDraft;
  status: 'pending';
  /** A rebuttal is NEVER auto-sent — a human approves it first. */
  requiresApproval: true;
  note: string;
}

export type ReplyAction =
  | AutoUnenrollAction
  | PauseEnrollmentAction
  | RouteTaskAction
  | QueueRebuttalDraftAction;

/** Everything handleReply needs, injected — no store, no firebase. */
export interface ReplyHandlingContext {
  /** Sequence ids the contact is actively enrolled in (unenroll scope). */
  activeEnrollments: string[];
  /** Injected clock (epoch ms); wall clock when omitted. */
  now?: number;
  contactName?: string;
  accountName?: string;
  /** Task-router seam: pick the least-loaded human owner. Null = triage. */
  routeOwner?: () => string | null;
  /** Playbook the rebuttal grounds in. Default: ONBOARDING_PLAYBOOK. */
  playbook?: PlaybookStep[];
}

// ===========================================================================
// heuristicIntent — the deterministic fallback classifier
// ===========================================================================

const UNSUBSCRIBE_RE =
  /\b(unsubscribe|opt[\s-]?out|remove me|take me off|stop (emailing|contacting|messaging|sending))\b/i;

const OOO_RE =
  /\b(out of (the )?office|ooo|on (vacation|holiday|leave)|(parental|annual|medical) leave|away until|back (on|in) |returning (on|to the office))\b/i;

const WRONG_PERSON_RE =
  /\b(no longer (at|with|work)|wrong person|not the (right|best) (person|contact)|left the company|i'?ve moved on|try reaching)\b/i;

/** Negative keyword set — objections. Checked BEFORE the positive set so
 *  "not interested" never reads as interest. */
const NEGATIVE_RE =
  /\b(not interested|no budget|too (expensive|pricey|costly)|not a (priority|fit|good fit)|already (use|have|work with)|happy with (our|the) current|pass for now|not right now|maybe (next|later)|went with (a )?competitor)\b/i;

/** Positive keyword set — buying signals. */
const POSITIVE_RE =
  /\b(interested|sounds (great|good)|let'?s (talk|chat|connect|schedule|book)|book (a|some) (call|time|meeting|demo)|send (me|over)|would love|happy to (chat|talk|meet)|keen|works for me|tell me more|schedule a)\b/i;

const MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

/** Extract a return date from OOO text; null when nothing parses. */
function parseReturnDate(text: string, nowMs: number): number | null {
  // ISO date: "until 2031-06-01"
  const iso = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (iso) {
    const t = Date.parse(`${iso[1]}T00:00:00Z`);
    if (Number.isFinite(t)) return t;
  }
  // Month name + day: "back on June 3rd", "returning Mar 14"
  const named = text.match(
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?\b/i,
  );
  if (named) {
    const month = MONTHS.indexOf(named[1].toLowerCase());
    const day = Number(named[2]);
    if (month >= 0 && day >= 1 && day <= 31) {
      const year = new Date(nowMs).getUTCFullYear();
      let t = Date.UTC(year, month, day);
      if (t <= nowMs) t = Date.UTC(year + 1, month, day);
      return t;
    }
  }
  // Numeric m/d or m/d/yyyy: "back 6/3", "until 06/03/2031"
  const slash = text.match(/\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/);
  if (slash) {
    const month = Number(slash[1]) - 1;
    const day = Number(slash[2]);
    if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
      const year = slash[3]
        ? Number(slash[3].length === 2 ? `20${slash[3]}` : slash[3])
        : new Date(nowMs).getUTCFullYear();
      let t = Date.UTC(year, month, day);
      if (!slash[3] && t <= nowMs) t = Date.UTC(year + 1, month, day);
      return Number.isFinite(t) ? t : null;
    }
  }
  return null;
}

/**
 * Deterministic keyword/date classifier — the honest fallback when the
 * worker is unreachable. Order matters: consent stops first (unsubscribe),
 * then OOO (with return-date extraction → `resumeAfter`), then wrong-person,
 * then the NEGATIVE set (so "not interested" is an objection), then the
 * POSITIVE set, else neutral. Pure — clock injected via `nowMs`.
 */
export function heuristicIntent(
  text: string,
  nowMs: number = Date.now(),
): { intent: ReplyIntent; confidence: number; resumeAfter?: number } {
  if (UNSUBSCRIBE_RE.test(text)) return { intent: 'unsubscribe', confidence: 0.95 };
  if (OOO_RE.test(text)) {
    const resumeAfter =
      parseReturnDate(text, nowMs) ?? nowMs + DEFAULT_OOO_RESUME_DAYS * DAY_MS;
    return { intent: 'ooo', confidence: 0.9, resumeAfter };
  }
  if (WRONG_PERSON_RE.test(text)) return { intent: 'wrong_person', confidence: 0.85 };
  if (NEGATIVE_RE.test(text)) return { intent: 'objection', confidence: 0.8 };
  if (POSITIVE_RE.test(text)) return { intent: 'interested', confidence: 0.8 };
  return { intent: 'neutral', confidence: 0.4 };
}

// ===========================================================================
// classifyReply — worker-first, deterministic fallback
// ===========================================================================

/** Injectable worker seam — structurally satisfied by the real argoWorker
 *  module (isConfigured + runAgent), so `classifyReply(r, argoWorker)` wires
 *  the live pipeline while tests inject a stub. */
export interface ReplyClassifierWorker {
  isConfigured?: () => boolean;
  runAgent(
    agent: { id: string; name: string; template: string; persona: string },
    snapshot: unknown,
  ): Promise<{ text: string }>;
}

/** The agent descriptor the real worker runs for reply classification. */
export const REPLY_CLASSIFIER_AGENT = {
  id: 'sequence-reply-classifier',
  name: 'Sequence Reply Classifier',
  template: 'reply-intent-classification',
  persona:
    'Precise email-reply triage analyst. Classify the reply into exactly one ' +
    'intent: interested | objection | ooo | unsubscribe | wrong_person | neutral. ' +
    'Respond ONLY with JSON: {"intent": string, "confidence": number, ' +
    '"resumeAfterDays": number?} — resumeAfterDays only for ooo.',
} as const;

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/** Parse + shape-check the worker's JSON verdict; null when malformed. */
function parseWorkerVerdict(
  text: string,
  nowMs: number,
): { intent: ReplyIntent; confidence: number; resumeAfter?: number } | null {
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(match[0]);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  const intent = v.intent;
  if (typeof intent !== 'string' || !REPLY_INTENTS.includes(intent as ReplyIntent)) {
    return null;
  }
  const confidence = clamp01(typeof v.confidence === 'number' ? v.confidence : 0.5);
  const out: { intent: ReplyIntent; confidence: number; resumeAfter?: number } = {
    intent: intent as ReplyIntent,
    confidence,
  };
  if (intent === 'ooo') {
    const days =
      typeof v.resumeAfterDays === 'number' && Number.isFinite(v.resumeAfterDays)
        ? Math.max(1, v.resumeAfterDays)
        : DEFAULT_OOO_RESUME_DAYS;
    out.resumeAfter = nowMs + days * DAY_MS;
  }
  return out;
}

/**
 * Classify a reply's intent — the REAL worker when configured, degrading to
 * {@link heuristicIntent} on absence/error/malformed output. NEVER throws:
 * the autopilot must always reach a verdict, and the verdict always says
 * which engine produced it.
 */
export async function classifyReply(
  reply: InboundReply,
  worker?: ReplyClassifierWorker | null,
): Promise<ClassifiedReply> {
  const nowMs = reply.at ?? Date.now();
  const base = { contactId: reply.contactId, sequenceId: reply.sequenceId };

  if (worker && typeof worker.runAgent === 'function') {
    try {
      if (typeof worker.isConfigured !== 'function' || worker.isConfigured()) {
        const { text } = await worker.runAgent(REPLY_CLASSIFIER_AGENT, {
          reply: reply.text,
          contactId: reply.contactId,
          sequenceId: reply.sequenceId,
          ...(reply.contactName ? { contactName: reply.contactName } : {}),
          ...(reply.accountName ? { accountName: reply.accountName } : {}),
        });
        const verdict = parseWorkerVerdict(text, nowMs);
        if (verdict) {
          return {
            ...base,
            intent: verdict.intent,
            confidence: verdict.confidence,
            ...(verdict.resumeAfter !== undefined
              ? { resumeAfter: verdict.resumeAfter }
              : {}),
            source: 'worker',
          };
        }
      }
    } catch {
      // Fall through to the deterministic classifier — never break the loop.
    }
  }

  const h = heuristicIntent(reply.text, nowMs);
  return {
    ...base,
    intent: h.intent,
    confidence: h.confidence,
    ...(h.resumeAfter !== undefined ? { resumeAfter: h.resumeAfter } : {}),
    source: 'fallback',
  };
}

// ===========================================================================
// buildAutoUnenrollReversal — the ledgered undo
// ===========================================================================

/**
 * Seal an auto-unenroll's before-state (the prior enrollment list) into a
 * ledger entry and compile the compensating `restore_enrollments` proposal.
 * Follows reversal.ts discipline exactly: deterministic ids (`undo-<entry>`),
 * the {@link REVERSAL_NOTE_PREFIX} marker note, `origin: 'reversal'`, and a
 * `status: 'pending'` proposal that must pass the approval queue — a
 * reversal is just another governed proposal, never a direct write.
 */
export function buildAutoUnenrollReversal(
  contactId: string,
  priorEnrollments: string[],
): ReversalPlan {
  const enrollments = [...priorEnrollments];
  const digest = fnvHash('', JSON.stringify([contactId, enrollments]));
  const entryId = `unenroll-${contactId}-${digest.replace(/^fnv1a:/, '')}`;
  const entry: AutoUnenrollLedgerEntry = {
    id: entryId,
    at: new Date().toISOString(),
    actionType: 'auto_unenroll',
    recordId: contactId,
    before: { enrollments },
  };
  const restore: RestoreEnrollmentsProposal = {
    id: `undo-${entryId}`,
    action: { kind: 'restore_enrollments', contactId, sequenceIds: enrollments },
    origin: 'reversal',
    reversesEntryId: entryId,
    note: `${REVERSAL_NOTE_PREFIX}${entryId} — restore ${enrollments.length} sequence enrollment${enrollments.length === 1 ? '' : 's'} for ${contactId}`,
    status: 'pending',
  };
  return { entry, restore, reversibility: classifyReversibility('auto_unenroll') };
}

/** The enrollment list a reversal plan would put back — the proven undo. */
export function restoredEnrollments(plan: ReversalPlan): string[] {
  return [...plan.restore.action.sequenceIds];
}

// ===========================================================================
// handleReply — the pure autopilot decision
// ===========================================================================

function uniq(ids: string[]): string[] {
  return [...new Set(ids.filter((s) => s !== ''))];
}

function displayName(classified: ClassifiedReply, ctx: ReplyHandlingContext): string {
  return ctx.contactName ?? classified.contactId;
}

/** Playbook-grounded rebuttal draft — cites real playbook steps, invents nothing. */
function draftRebuttal(
  classified: ClassifiedReply,
  ctx: ReplyHandlingContext,
): RebuttalDraft {
  const steps = (ctx.playbook ?? ONBOARDING_PLAYBOOK).slice(0, 3);
  const name = displayName(classified, ctx);
  const proofLines = steps
    .map((s) => `  • ${s.title} (day ${s.offsetDays}): ${s.note}`)
    .join('\n');
  return {
    subject: `Re: your note — how we de-risk this`,
    body:
      `Hi ${name},\n\nTotally fair pushback. Rather than argue, here is exactly how ` +
      `the first weeks of an engagement run, straight from our delivery playbook:\n\n` +
      `${proofLines}\n\nIf the concern still stands after seeing that plan, no hard ` +
      `feelings — but most teams find the structure answers it. Open to a short call?\n`,
    groundedIn: steps.map((s) => s.title),
  };
}

/**
 * Map a classified reply to governed actions. PURE — emits decisions, never
 * executes them:
 *
 *   • interested / unsubscribe → ONE reversible, ledgered auto-unenroll
 *     across ALL active sequences (a live conversation / a legal stop makes
 *     every cadence touch harmful). Interested additionally routes a
 *     follow-up task so a human picks up the live thread.
 *   • ooo → pause the replying sequence with a scheduled resume at
 *     `resumeAfter` (parsed return date, or the default window).
 *   • objection → route a high-priority task via the injected task-router
 *     owner pick + queue a playbook-grounded rebuttal DRAFT for approval —
 *     never a send.
 *   • wrong_person → pause + route a data-hygiene task to find the right
 *     contact.
 *   • neutral → pause for human review (ANY reply stops the cadence — the
 *     lead-burning bug is precisely a cadence that keeps firing).
 */
export function handleReply(
  classified: ClassifiedReply,
  ctx: ReplyHandlingContext,
): ReplyAction[] {
  const now = ctx.now ?? Date.now();
  const name = displayName(classified, ctx);
  const allSequences = uniq([classified.sequenceId, ...ctx.activeEnrollments]);

  switch (classified.intent) {
    case 'interested':
    case 'unsubscribe': {
      const reason =
        classified.intent === 'unsubscribe'
          ? `${name} asked to unsubscribe — hard legal stop; every sequence stands down.`
          : `${name} replied positively — a human conversation is live; autopilot stands down across all sequences.`;
      const unenroll: AutoUnenrollAction = {
        kind: 'auto_unenroll',
        contactId: classified.contactId,
        sequenceIds: allSequences,
        reason,
        reversible: true,
        reversal: buildAutoUnenrollReversal(classified.contactId, allSequences),
      };
      if (classified.intent === 'unsubscribe') return [unenroll];
      const owner = ctx.routeOwner?.() ?? null;
      const task: CreateTaskAction = {
        kind: 'create_task',
        title: `Positive reply from ${name} — pick up the live thread`,
        priority: 'high',
        assignee: owner,
        personId: classified.contactId,
        ...(ctx.contactName ? { personName: ctx.contactName } : {}),
        ...(ctx.accountName ? { accountName: ctx.accountName } : {}),
      };
      return [
        unenroll,
        {
          kind: 'route_task',
          task,
          owner,
          note: `Reply intent 'interested' (${Math.round(classified.confidence * 100)}%) — routed to ${owner ?? 'triage'}.`,
        },
      ];
    }

    case 'ooo': {
      const resumeAfter =
        classified.resumeAfter ?? now + DEFAULT_OOO_RESUME_DAYS * DAY_MS;
      return [
        {
          kind: 'pause_enrollment',
          contactId: classified.contactId,
          sequenceId: classified.sequenceId,
          resumeAfter,
          reason: `${name} is out of office — paused with a scheduled resume on ${new Date(resumeAfter).toISOString().slice(0, 10)}.`,
        },
      ];
    }

    case 'objection': {
      const owner = ctx.routeOwner?.() ?? null;
      const task: CreateTaskAction = {
        kind: 'create_task',
        title: `Handle objection from ${name}`,
        priority: 'high',
        assignee: owner,
        personId: classified.contactId,
        ...(ctx.contactName ? { personName: ctx.contactName } : {}),
        ...(ctx.accountName ? { accountName: ctx.accountName } : {}),
      };
      return [
        {
          kind: 'route_task',
          task,
          owner,
          note: `Reply intent 'objection' (${Math.round(classified.confidence * 100)}%) — routed to ${owner ?? 'triage'} (task-router: least-loaded human).`,
        },
        {
          kind: 'queue_rebuttal_draft',
          contactId: classified.contactId,
          sequenceId: classified.sequenceId,
          draft: draftRebuttal(classified, ctx),
          status: 'pending',
          requiresApproval: true,
          note: 'Playbook-grounded rebuttal draft — pending human approval; nothing sends automatically.',
        },
      ];
    }

    case 'wrong_person': {
      const owner = ctx.routeOwner?.() ?? null;
      const task: CreateTaskAction = {
        kind: 'create_task',
        title: `Wrong contact: ${name} — find the right person`,
        priority: 'medium',
        assignee: owner,
        personId: classified.contactId,
        ...(ctx.contactName ? { personName: ctx.contactName } : {}),
        ...(ctx.accountName ? { accountName: ctx.accountName } : {}),
      };
      return [
        {
          kind: 'pause_enrollment',
          contactId: classified.contactId,
          sequenceId: classified.sequenceId,
          reason: `${name} says they are not the right contact — paused for data hygiene.`,
        },
        { kind: 'route_task', task, owner, note: 'Wrong-person reply — update the CRM record.' },
      ];
    }

    case 'neutral':
    default:
      return [
        {
          kind: 'pause_enrollment',
          contactId: classified.contactId,
          sequenceId: classified.sequenceId,
          reason: `${name} replied — cadence paused for human review (a reply always stops the autopilot).`,
        },
      ];
  }
}
